import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Payments as PaymentsIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
  Rectangle,
} from 'recharts';
import { AdminService } from '../../services/adminService';

/**
 * Clients payants : nouveaux clients par jour (empilés par boutique), clients avec un accès payé
 * en cours, MRR, derniers achats. Les définitions font foi côté serveur
 * (`services/clientsPayants.service.js`) ; l'Entonnoir d'achat raconte le chemin, ici on compte.
 */

// Une teinte par boutique, dans l'ordre fixe de la palette catégorielle (bleu, orange, aqua).
const BOUTIQUES = [
  { cle: 'stripe', libelle: 'Stripe (web)', couleur: '#2a78d6' },
  { cle: 'apple', libelle: 'App Store', couleur: '#eb6834' },
  { cle: 'google', libelle: 'Google Play', couleur: '#1baf7a' },
];
const LIBELLE_BOUTIQUE = Object.fromEntries(BOUTIQUES.map((b) => [b.cle, b.libelle]));
const COULEUR_ABONNES = '#2a78d6';
const COULEUR_PASSES = '#86b6ef';
const COULEUR_MRR = '#1c5cab';

const euros = (v) => (v ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

// Un segment vide n'est pas dessiné : son liseré blanc hacherait l'axe du bas.
const Segment = (props) => (props.height > 0 ? <Rectangle {...props} /> : null);

// 'YYYY-MM-DD' lu à midi : un minuit UTC basculerait la veille selon le fuseau.
const formatJour = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';
const formatJourLong = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '—';

function Pastille({ couleur }) {
  return <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: couleur, flexShrink: 0 }} />;
}

function CadreInfobulle({ label, children }) {
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{formatJourLong(label)}</Typography>
      {children}
    </Box>
  );
}

function InfobulleNouveaux({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const l = payload[0].payload;
  return (
    <CadreInfobulle label={label}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Nouveaux clients : <strong>{l.stripe + l.apple + l.google}</strong>
        {' '}({l.abonnements} abonnement{l.abonnements > 1 ? 's' : ''}, {l.achats} achat{l.achats > 1 ? 's' : ''} unique{l.achats > 1 ? 's' : ''})
      </Typography>
      {BOUTIQUES.map((b) => (
        <Box key={b.cle} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Pastille couleur={b.couleur} />
          <Typography variant="body2">{b.libelle} : <strong>{l[b.cle]}</strong></Typography>
        </Box>
      ))}
    </CadreInfobulle>
  );
}

function InfobulleActifs({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const l = payload[0].payload;
  return (
    <CadreInfobulle label={label}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>Clients payants : <strong>{l.payants}</strong></Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Pastille couleur={COULEUR_ABONNES} />
        <Typography variant="body2">Abonnés : <strong>{l.abonnes}</strong></Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Pastille couleur={COULEUR_PASSES} />
        <Typography variant="body2">Pass en cours : <strong>{l.passes}</strong></Typography>
      </Box>
    </CadreInfobulle>
  );
}

function InfobulleMrr({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const l = payload[0].payload;
  return (
    <CadreInfobulle label={label}>
      <Typography variant="body2">MRR : <strong>{euros(l.mrr)}</strong> ({l.abonnes} abonné{l.abonnes > 1 ? 's' : ''})</Typography>
    </CadreInfobulle>
  );
}

const axeX = {
  dataKey: 'jour',
  tick: { fontSize: 11 },
  tickFormatter: formatJour,
  angle: -45,
  textAnchor: 'end',
  height: 50,
  minTickGap: 10,
};

function Chiffre({ titre, valeur, detail }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">{titre}</Typography>
      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>{valeur}</Typography>
      {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
    </Box>
  );
}

export default function ClientsPayantsSection({ segment = 'familles' }) {
  const [jours, setJours] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    AdminService.getPaidCustomers(jours, segment)
      .then((d) => { if (!annule) { setData(d); setError(null); } })
      .catch((err) => {
        console.error('Error fetching paid customers:', err);
        if (!annule) setError('Impossible de charger les clients payants');
      })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [jours, segment]);

  const serie = data?.serie ?? [];
  const auj = data?.aujourdhui ?? { abonnes: 0, passes: 0, mrr: 0, mrrEstime: 0 };
  const nouveauxPar = Object.fromEntries(BOUTIQUES.map((b) => [b.cle, serie.reduce((s, l) => s + l[b.cle], 0)]));
  const nouveaux = BOUTIQUES.reduce((s, b) => s + nouveauxPar[b.cle], 0);
  const debut = serie[0] ?? { payants: 0, mrr: 0 };

  return (
    <Card>
      <CardHeader
        title="Clients payants"
        subheader="Client = le compte qui paie (ou l'adresse d'un achat sans compte). Nouveau client = jour de son premier paiement, abonnement ou achat unique, rangé sous la boutique de ce paiement. MRR = prix catalogue TTC des abonnements en cours ramené au mois (un annuel compte pour 1/12), avant commission des boutiques et codes promo ; les pass n'y entrent pas."
        avatar={<PaymentsIcon color="success" />}
        action={
          <ToggleButtonGroup
            value={jours}
            exclusive
            size="small"
            onChange={(e, v) => { if (v) setJours(v); }}
            sx={{ pr: 1 }}
          >
            <ToggleButton value={30}>30 j</ToggleButton>
            <ToggleButton value={90}>90 j</ToggleButton>
            <ToggleButton value={180}>180 j</ToggleButton>
            <ToggleButton value={365}>1 an</ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Chiffre
                  titre="Clients payants aujourd'hui"
                  valeur={auj.abonnes + auj.passes}
                  detail={`${auj.abonnes} abonné${auj.abonnes > 1 ? 's' : ''} · ${auj.passes} pass · ${debut.payants} il y a ${jours} j`}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Chiffre
                  titre="MRR"
                  valeur={euros(auj.mrr)}
                  detail={`${euros(debut.mrr)} il y a ${jours} j${auj.mrrEstime ? ` · ${auj.mrrEstime} abonnement${auj.mrrEstime > 1 ? 's' : ''} sans produit, compté${auj.mrrEstime > 1 ? 's' : ''} au prix mensuel` : ''}`}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Chiffre
                  titre={`Nouveaux clients sur ${jours} j`}
                  valeur={nouveaux}
                  detail={BOUTIQUES.map((b) => `${b.libelle} ${nouveauxPar[b.cle]}`).join(' · ')}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Nouveaux clients par jour</Typography>
            <Box sx={{ height: 300, width: '100%', mb: 3 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serie} barCategoryGap={1}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis {...axeX} />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip content={<InfobulleNouveaux />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
                  <Legend />
                  {BOUTIQUES.map((b, i) => (
                    <Bar
                      key={b.cle}
                      dataKey={b.cle}
                      name={b.libelle}
                      stackId="boutiques"
                      fill={b.couleur}
                      stroke="#ffffff"
                      strokeWidth={1}
                      radius={i === BOUTIQUES.length - 1 ? [3, 3, 0, 0] : 0}
                      shape={<Segment />}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* Deux mesures d'échelles différentes : deux graphiques, jamais un double axe. */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Clients payants en cours</Typography>
                <Box sx={{ height: 280, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={serie}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis {...axeX} />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip content={<InfobulleActifs />} />
                      <Legend />
                      <Area type="stepAfter" dataKey="abonnes" name="Abonnés" stackId="payants"
                        stroke={COULEUR_ABONNES} strokeWidth={2} fill={COULEUR_ABONNES} fillOpacity={0.35} />
                      <Area type="stepAfter" dataKey="passes" name="Pass en cours" stackId="payants"
                        stroke={COULEUR_PASSES} strokeWidth={2} fill={COULEUR_PASSES} fillOpacity={0.35} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>MRR (€ par mois)</Typography>
                <Box sx={{ height: 280, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={serie}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis {...axeX} />
                      <YAxis tickFormatter={(v) => `${v} €`} width={60} />
                      <RechartsTooltip content={<InfobulleMrr />} />
                      <Line type="stepAfter" dataKey="mrr" name="MRR" stroke={COULEUR_MRR} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Derniers achats</Typography>
            {(data?.derniers ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">Aucun achat.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quand</TableCell>
                    <TableCell>Boutique</TableCell>
                    <TableCell>Produit</TableCell>
                    <TableCell>Compte</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.derniers.map((a, i) => (
                    <TableRow key={`${a.quand}-${i}`}>
                      <TableCell>{new Date(a.quand).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</TableCell>
                      <TableCell>{LIBELLE_BOUTIQUE[a.boutique] ?? a.boutique}</TableCell>
                      <TableCell>{a.produitLibelle}</TableCell>
                      <TableCell>
                        {a.uid ? (
                          <>
                            {a.nom || '—'}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">{a.uid}</Typography>
                          </>
                        ) : (
                          <Typography component="span" variant="body2" color="text.secondary">sans compte</Typography>
                        )}
                      </TableCell>
                      <TableCell>{a.nouveau ? <strong>Nouveau client</strong> : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
